import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/ui/Spinner';
import { useProfile } from '@/features/auth/useProfile';
import {
  CeoDashboard,
  ManagerDashboard,
  SalesDashboard,
  TeamLeaderDashboard,
} from '@/features/reports/Dashboards';
import { FinanceDashboard, MarketingDashboard } from '@/features/reports/RoleDashboards';

/**
 * The right dashboard for who is looking (#32–#35).
 *
 * The four are genuinely different questions, not one dashboard with things
 * hidden: a CEO wants twelve months and a forecast, a manager wants who is
 * leaking leads, a team leader wants their own people, and a rep wants their own
 * targets. Showing a rep a company revenue tile they cannot act on is noise.
 *
 * Marketing and finance get their own rather than the manager view, and not for
 * cosmetic reasons: neither can read `deals` under RLS, so the manager dashboard's
 * revenue-per-agent table would show them a wall of zeros that reads as "the
 * company sold nothing". A dashboard that silently reports zero because of RLS is
 * worse than one that leaves the number out.
 */
export default function DashboardPage() {
  const { t } = useTranslation();
  const { data: profile, isPending } = useProfile();

  if (isPending) return <Spinner />;

  const role = profile?.role;

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{t('pages.dashboard.title')}</h1>
        <p className="text-sm text-slate-600">
          {t('pages.dashboard.welcome', { name: profile?.full_name ?? '' })}
        </p>
      </div>

      {role === 'ceo' ? (
        <CeoDashboard />
      ) : role === 'sales' ? (
        <SalesDashboard />
      ) : role === 'team_leader' ? (
        <TeamLeaderDashboard />
      ) : role === 'marketing' ? (
        <MarketingDashboard />
      ) : role === 'finance' ? (
        <FinanceDashboard />
      ) : (
        <ManagerDashboard />
      )}
    </section>
  );
}
